import { plainToClass } from "class-transformer";
import { IFilter, InitializeFilterDropdown } from "../../../shared/scripts/dropdown-functions";
import { TableColumnGroup, TableColumnVisibilityControllerV2 } from "../../../shared/scripts/table-column-visibility";
import _ from 'lodash';

import { FilterTab } from "./list-management.filters";
import { AddNewController, EditController } from "./list-management.controllers";


// --- Utilities
export class JsonItem {
    Id: string;
    Json: any;
}

export class PickListItem {
    Id: number;
    Value: string;
}

export class PickListItemSorted {
    Id: number;
    Value: string;
    SortOrder: number;
}

export class PickListItemString {
    Id: string;
    Value: string;
}

export class ListInformation {
    id: string;
    propId: string;
    displayName: string
    handler: string;
    columns: any[];
    tableColumnGroups: TableColumnGroup[];
    filterTabs: FilterTab[];
    addNewModalId: string
    addNewController: AddNewController;
    editModalId: string;
    editController: EditController;
    defaultItem: any;
}

export class ListItem {
    Id: number;
    IsSelected: boolean = false;
    SelectorDisplay: string = `<div class="form-check"><input class="form-check-input" type="checkbox"></div>`;

    updateSelect(isSelected: boolean): void {
        this.IsSelected = isSelected;
        if (this.IsSelected) {
            this.SelectorDisplay = `<div class="form-check"><input class="form-check-input" type="checkbox" checked></div>`;
        }
        else {
            this.SelectorDisplay = `<div class="form-check"><input class="form-check-input" type="checkbox"></div>`;
        }
    }
}

export interface IControllerFilter {
    pickLists: JsonItem[]; // Pick Lists used for the list
    triggerFilter(): void;
}

export class CombineDisplayOption {
    propName: string;
    pickListName: string;
    endContent: string;
    prevContent: string;
    constructor(propName: string, pickListName: string, endContent: string = "<br>", prevContent: string = "") {
        this.propName = propName;
        this.pickListName = pickListName;
        this.endContent = endContent;
        this.prevContent = prevContent;
    }
}

export class FileUploadMap {
    file: File;
    propertyName: string;
}

//----- Users
export class AppUserExtended {
    AppUser: AppUserRedacted = new AppUserRedacted();
    AppUserAffiliationList: AppUserAffiliation[] = [];
    AppUserGroupList: AppUserGroupCommittee[] = [];
    AppUserAccessList: AppUserAccess[] = [];
    AppUserRoleList: string[] = ["General"];

    static createFromRequest(request: UserRequestExtended): AppUserExtended {
        let appUserRedacted: AppUserRedacted = AppUserRedacted.createFromRequest(request);
        let affiliationList: AppUserAffiliation[] = AppUserAffiliation.createFromRequest(request);
        let appUserGroupList: AppUserGroupCommittee[] = AppUserGroupCommittee.createFromRequest(request);
        let appUserRoleList: string[] = ["General"];
        let appUserAccessList: AppUserAccess[] = [];

        let appUserExtended: AppUserExtended = {
            AppUser: appUserRedacted,
            AppUserAffiliationList: affiliationList,
            AppUserGroupList: appUserGroupList,
            AppUserRoleList: appUserRoleList,
            AppUserAccessList: appUserAccessList
        }

        return appUserExtended;
    }
}

export class AppUserRedacted {
    Id: number = 0;
    FirstName: string = "";
    LastName: string = "";
    CreateDate: Date = new Date();
    IsActive: boolean = true;
    IsVisible: boolean = true;
    OrcidLink: string = "";
    BioLink: string = "";
    LinkedinLink: string = "";
    SentWelcomeEmail: boolean = false;
    SentWelcomeEmailDate: Date = new Date(1, 1, 1);
    Email: string = "";
    TerminalDegreeIds: string = "";
    Expertise: any = "";
    ProfilePicturePath: string = "";
    OrganizationIds: string = "";
    ProjectManagerCenterIds: string = "";
    LastLoginDate: Date = new Date(1, 1, 1);
    AttestationLastUpdated: Date = new Date(1, 1, 1);
    HasAcceptedAttestation: boolean = false;

    static createFromRequest(request: UserRequestExtended): AppUserRedacted {
        let appUserRedacted: AppUserRedacted = new AppUserRedacted();

        appUserRedacted.FirstName = request.UpdateUserRequest.FirstName;
        appUserRedacted.LastName = request.UpdateUserRequest.LastName;
        appUserRedacted.Email = request.UpdateUserRequest.Email;
        appUserRedacted.OrganizationIds = request.UpdateUserRequest.OrganizationIds;
        appUserRedacted.TerminalDegreeIds = request.UpdateUserRequest.TerminalDegreeIds;
        appUserRedacted.OrcidLink = request.UpdateUserRequest.OrcidLink;
        appUserRedacted.LinkedinLink = request.UpdateUserRequest.LinkedinLink;
        appUserRedacted.BioLink = request.UpdateUserRequest.BioLink;

        return appUserRedacted;
    }
}

export class AppUserAffiliation {
    Id: number = 0;
    AppUserId: number = 0;
    CenterId: number = 0;
    CoreId: number = 0;
    ProjectId: number = 0;
    RoleId: number = 0;

    static createFromRequest(request: UserRequestExtended): AppUserAffiliation[] {
        let affiliations: AppUserAffiliation[] = [];

        request.UpdateAffiliationRequestList.forEach(affiliation => {
            let newAffiliation: AppUserAffiliation = new AppUserAffiliation();
            newAffiliation.AppUserId = affiliation.AppUserId;
            newAffiliation.CenterId = affiliation.CenterId;
            newAffiliation.CoreId = affiliation.CoreId;
            newAffiliation.ProjectId = affiliation.ProjectId;
            newAffiliation.RoleId = affiliation.RoleId;

            affiliations.push(newAffiliation);
        });

        return affiliations;
    }
}

export class AppUserGroupCommittee {
    Id: number = 0;
    AppUserId: number = 0;
    GroupId: number = 0;
    GroupRoleId: number = 0;

    static createFromRequest(request: UserRequestExtended): AppUserGroupCommittee[] {
        let groups: AppUserGroupCommittee[] = [];

        request.UpdateGroupRequestList.forEach(group => {
            if (group.IsNew) {
                let newGroup: AppUserGroupCommittee = new AppUserGroupCommittee();
                newGroup.AppUserId = group.AppUserId;
                newGroup.GroupId = group.GroupId;
                newGroup.GroupRoleId = group.GroupRoleId;

                groups.push(newGroup);
            }
        });

        return groups;
    }
}

export class AppUserAccess {
    Id: number = 0;
    CenterId: number = 0;
    AppUserId: number = 0;
    AccessTypeId: number = 0;
}


//------ User Requests
export class UserRequestExtended {
    UserRequest: UserRequest = new UserRequest();
    UpdateUserRequest: UpdateUserRequest = new UpdateUserRequest();
    UpdateGroupRequestList: UpdateGroupRequest[] = [];
    UpdateAffiliationRequestList: UpdateAffiliationRequest[] = [];
    AppUserExtended: AppUserExtended = null;
}

export class UserRequest {
    Id: number = 0;
    RequestCenterIds: string = "";
    StatusId: number = 0;
    TypeId: number = 0;
    RequestUserId: number = 0;
    CreateDate: Date = new Date();
    LastUpdated: Date = new Date();
}

export class UpdateUserRequest {
    Id: number = 0;
    UserRequestId: number = 0;
    AppUserId: number = 0;
    FirstName: string = "";
    LastName: string = "";
    Email: string = "";
    IsActive: boolean = true;
    IsVisible: boolean = true;
    OrcidLink: string = "";
    BioLink: string = "";
    LinkedinLink: string = "";
    TerminalDegreeIds: string = "";
    OrganizationIds: string = "";
}

export class UpdateGroupRequest {
    Id: number = 0;
    UserRequestId: number = 0;
    AppUserId: number = 0;
    GroupId: number = 0;
    GroupRoleId: number = 0;
    IsNew: boolean = true;
}

export class UpdateAffiliationRequest {
    Id: number = 0;
    UserRequestId: number = 0;
    AppUserId: number = 0;
    RoleId: number = 0;
    CenterId: number = 0;
    CoreId: number = 0;
    ProjectId: number = 0;
}


//----- Centers, Cores, Projects
export class RevamppCenter {
    Id: number = 0;
    VersionId: number = 0;
    StatusId: number = 0;
    StatusDate: Date = new Date();
    LastUpdated: Date = new Date();
    LastReviewed: Date = new Date();
    LastReviewedUserId: number = 0;
    CenterAbbreviation: string = "";
    CenterName: string = "";
    AwardNumber: string = "";
    AbstractDescription: string = "";
    PublicDescription: string = "";
    OpportunityNumber: string = "";
    ReporterUrl: string = "";
    ProgramOfficerId: number = 0;
    OrgName: string = "";
    OrgTypeId: number = 0;
    OrgCountryId: number = 0;
    OrgStateId: number = 0;
    OrgCity: string = "";
    OrgLatitude: number;
    OrgLongitude: number;
    ExternalWebsiteUrl: string = "";
    IsApprovedHumanSubjects: number = 3;
    IsApprovedVertebrateAnimals: number = 3;
    IsApprovedSelectAgents: number = 3;
    HasSharingAgreement: number = 3;
    VirusFamilyFocusIds: string = "";
    PrototypeVirusIds: string = "";
    TestCaseVirusIds: string = "";
    VaccineApproachesIds: string = "";
    AntibodyApproachesIds: string = "";
    YearOneStart: Date = new Date();
    YearOneEnd: Date = new Date();
    YearTwoStart: Date = new Date();
    YearTwoEnd: Date = new Date();
    YearThreeStart: Date = new Date();
    YearThreeEnd: Date = new Date();
    YearFourStart: Date = new Date();
    YearFourEnd: Date = new Date();
    YearFiveStart: Date = new Date();
    YearFiveEnd: Date = new Date();
    ParticipatingOrgIds: string = "";
}

export class RevamppCore {
    Id: number = 0;
    StatusId: number = 0;
    StatusDate: Date = new Date();
    LastUpdated: Date = new Date();
    LastReviewed: Date = new Date();
    LastReviewedUserId: number = 0;
    CenterId: number = 0;
    CoreName: string = "";
    AbstractDescription: string = "";
    PublicDescription: string = "";;
    OrgName: string = "";
    OrgTypeId: number = 0;
    OrgCountryId: number = 0;
    OrgStateId: number = 0;
    OrgCity: string = "";
    OrgLatitude: number;
    OrgLongitude: number;
    CoreServicesIds: string = "";
    IsApprovedHumanSubjects: number = 3;
    IsApprovedVertebrateAnimals: number = 3;
    IsApprovedSelectAgents: number = 3;
    VirusFamilyFocusIds: string = "";
    PrototypeVirusIds: string = "";
    TestCaseVirusIds: string = "";
    VaccineApproachesIds: string = "";
    AntibodyApproachesIds: string = "";
}

export class RevamppProject {
    Id: number = 0;
    StatusId: number = 0;
    StatusDate: Date = new Date();
    LastUpdated: Date = new Date();
    LastReviewed: Date = new Date();
    LastReviewedUserId: number = 0;
    CenterId: number = 0;
    ProjectName: string = "";
    AbstractDescription: string = "";
    PublicDescription: string = "";;
    OrgName: string = "";
    OrgTypeId: number = 0;
    OrgCountryId: number = 0;
    OrgStateId: number = 0;
    OrgCity: string = "";
    OrgLatitude: number;
    OrgLongitude: number;
    IsApprovedHumanSubjects: number = 3;
    IsApprovedVertebrateAnimals: number = 3;
    IsApprovedSelectAgents: number = 3;
    VirusFamilyFocusIds: string = "";
    PrototypeVirusIds: string = "";
    TestCaseVirusIds: string = "";
    VaccineApproachesIds: string = "";
    AntibodyApproachesIds: string = "";
}

export class Country {
    Id: number = 0;
    CountryName: string = "";
    CountryCode: string = "";
    RegionId: number = 0;
}


// ---- Viruses
export class RevamppVirus {
    Id: number = 0;
    VirusName: string = "";
    Abbreviation: string = "";
    TaxonomyId: string = "";
    FamilyId: number = 0;
    DiseaseName: string = "";
    DiseaseHostIds: string = "";
    VectorIds: string = "";
    BslId: number = 0;
    IsSelectAgent: number = 3;
    AnimalModelIds: string = "";
    AbslId: number = 0;
}

export class RevamppVirusFamily {
    Id: number = 0;
    OrderId: number = 0;
    ScientificName: string = "";
    CommonName: string = "";
    TaxonomyId: string = "";
    VirusTargetIds: string = "";
}

export class RevamppVirusTarget {
    Id: number = 0;
    FamilyId: number = 0;
    TargetId: number = 0;
}

// ----- Teams

export class TeamsAccessResponse {
    TeamDisplayName: string;
    TeamLink: string;
    IsMember: boolean;
}

export class TeamsAccessResponseAll {
    TeamDisplayName: string;
    TeamsLink: string;
    CurrentMembers: string[];
    MissingMembers: string[];
}

// Events
export class RevamppEvent {
    Id: number = 0;
    CenterId: number = 0;
    EventTitle: string = "";
    EventDate: Date = new Date();
    EventType: number = 0;
    FolderName: string = "";
    IsVisible: boolean = true;
}

// Animal Models
export class AnimalModel {
    Id: number = 0;
    LastUpdated: Date = new Date();
    IsVisible: boolean = true;
    ModelName: string = "";
    ModelPurposeIds: string = "";
    VirusFamilyId: number = 0;
    ChallengeVirusIds: string = "";
    VirusStrainDetails: string = "";
    VirusSource: string = "";
    VirusCharacterization: string = "";
    VirusDose: string = "";
    VirusRouteIds: string = "";
    SpeciesId: number = 0;
    StrainIds: string = "";
    StrainDetails: string = "";
    SourceIds: string = "";
    AbslId: number = 0;
    SexIds: string = "";
    Age: string = "";
    Weight: string = "";
    EndpointIds: string = "";
    EndpointDetails: string = "";
    CenterId: number = 0;
    CoreIds: string = "";
    ProjectIds: string = "";
    ChampionId: number = 0;
    ModelStatusId: number = 0;
    PositiveControl: string = "";
    PublicationIds: string = "";
    AdditionalInfo: string = "";
}

//Manuscipts
export class Manuscript {
    Id: number = 0;
    IsVisible: boolean = true;

    // Core metadata
    Title: string = "";

    // Ownership / authorship
    PrimaryAuthorId: number = 0;
    SeniorAuthorId: number = 0;
    NetworkAuthorIds: string = "";           
    CollaboratingCenterIds: string = "";    
    CenterId: number = 0;
    ProjectIds: string = "";                 
    CoreIds: string = "";                    
    StatusId: number = 0;

    // Journal and notes
    TargetJournal: string = "";
    Notes: string = "";

    // Published identifiers
    PubMedId: string = "";
    Pmcid: string = "";
    Doi: string = "";

    // Audit
    LastUpdated: Date = new Date();
    CreationDate: Date = new Date();
    AnticipatedDate?: string | null;
}



